/**
 * Injects a loading skeleton next to the PDP customer-reviews Yotpo widget and
 * removes it once the widget renders (or after a fallback timeout).
 *
 * On TJC the reviews widget is a Yotpo Shopify app block (`.yotpo-widget-instance`)
 * placed beside an empty `#reviews` anchor inside a cx-container, so the skeleton
 * cannot be rendered from theme Liquid in the right place — it is built and mounted
 * here in JS instead. Only the reviews widget associated with `#reviews` gets a
 * skeleton, avoiding duplicates when multiple Yotpo widgets exist on the page.
 *
 * @see assets/yotpo-reviews-skeleton.css
 */

/** @constant {string} */
const WIDGET_SELECTOR = '.yotpo-widget-instance';

/** @constant {string} */
const SKELETON_CLASS = 'yotpo-reviews-skeleton';

/** @constant {string} Containers that scope an anchor to its reviews widget. */
const CONTAINER_SELECTOR = '.shopify-section, .custom-container, .yotpo-product-reviews-section';

/** @constant {number} Min rendered widget height (px) treated as "loaded". */
const WIDGET_READY_MIN_HEIGHT = 40;

/** @constant {number} Safety net: remove the skeleton after this long regardless. */
const FALLBACK_HIDE_MS = 15000;

/** @constant {number} Number of review-card placeholders. */
const CARD_COUNT = 3;

/**
 * @returns {string}
 */
function skeletonMarkup() {
  const line = (mods = '') => `<div class="${SKELETON_CLASS}__line ${mods}"></div>`;

  const card = `
    <div class="${SKELETON_CLASS}__card">
      <div class="${SKELETON_CLASS}__card-head">
        <div class="${SKELETON_CLASS}__avatar"></div>
        ${line(`${SKELETON_CLASS}__line--w40`)}
      </div>
      ${line(`${SKELETON_CLASS}__line--stars`)}
      ${line(`${SKELETON_CLASS}__line--w90`)}
      ${line(`${SKELETON_CLASS}__line--w75`)}
    </div>`;

  return `
    <div class="${SKELETON_CLASS}__summary">
      <div class="${SKELETON_CLASS}__score">
        <div class="${SKELETON_CLASS}__score-number"></div>
        ${line(`${SKELETON_CLASS}__line--stars`)}
      </div>
      <div class="${SKELETON_CLASS}__bars">
        ${line(`${SKELETON_CLASS}__line--w90`)}
        ${line(`${SKELETON_CLASS}__line--w75`)}
        ${line(`${SKELETON_CLASS}__line--w60`)}
        ${line(`${SKELETON_CLASS}__line--w40`)}
      </div>
    </div>
    <div class="${SKELETON_CLASS}__cards">
      ${Array.from({ length: CARD_COUNT }, () => card).join('')}
    </div>`;
}

/**
 * @returns {HTMLElement}
 */
function buildSkeleton() {
  const el = document.createElement('div');
  el.className = SKELETON_CLASS;
  el.dataset.yotpoReviewsSkeleton = '';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-label', 'Loading reviews');
  el.innerHTML = skeletonMarkup();
  return el;
}

/**
 * @param {Element} widget
 * @returns {boolean}
 */
function widgetHasRendered(widget) {
  const hasElementChild = Array.from(widget.children).some((child) => child.nodeType === 1);
  if (!hasElementChild) return false;

  return /** @type {HTMLElement} */ (widget).offsetHeight >= WIDGET_READY_MIN_HEIGHT;
}

/**
 * Picks the reviews widget tied to `#reviews` (or the first one as a fallback).
 * @returns {Element | null}
 */
function findMainReviewsWidget() {
  const widgets = Array.from(document.querySelectorAll(WIDGET_SELECTOR));
  if (!widgets.length) return null;

  const anchor = document.getElementById('reviews');
  if (anchor) {
    const container = anchor.closest(CONTAINER_SELECTOR);
    const inContainer = container?.querySelector(WIDGET_SELECTOR);
    if (inContainer) return inContainer;

    const following = widgets.find(
      (w) => anchor.compareDocumentPosition(w) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    if (following) return following;
  }

  return widgets[0];
}

function init() {
  const widget = findMainReviewsWidget();
  if (!widget) return;

  const mount = widget.closest('.shopify-app-block') ?? widget;
  if (mount.parentElement?.querySelector(`.${SKELETON_CLASS}`)) return;
  if (widgetHasRendered(widget)) return;

  const skeleton = buildSkeleton();
  mount.parentElement?.insertBefore(skeleton, mount);

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    window.clearTimeout(fallbackTimer);
    skeleton.remove();
  };

  const observer = new MutationObserver(() => {
    if (widgetHasRendered(widget)) finish();
  });
  observer.observe(widget, { childList: true, subtree: true });

  const fallbackTimer = window.setTimeout(finish, FALLBACK_HIDE_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
